import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Download } from "lucide-react";
import Masthead from "@/components/alcott/Masthead";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import ShareBar from "@/components/alcott/ShareBar";
import Seo from "@/components/Seo";
import { ALL_ESSAYS } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

export const Essay = () => {
  const { slug } = useParams();
  const [waitlist, setWaitlist] = useState(false);
  const essay = ALL_ESSAYS.find((e) => e.internal && e.slug === slug);

  if (!essay) return <Navigate to="/archives" replace />;

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-[#0A0A0A]" data-testid="essay-page">
      <Seo
        title={essay.title}
        description={essay.excerpt || essay.body?.[0]?.slice(0, 155)}
        image={essay.image}
        type="article"
        article={{ author: essay.author, category: essay.category }}
      />
      <Masthead />

      <article className="mx-auto max-w-3xl px-5 py-16 md:px-8 md:py-24">
        <Link
          to="/archives"
          className="inline-flex items-center gap-2 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/50 transition-colors hover:text-[#C05C42]"
          data-testid="essay-back"
        >
          <ArrowLeft size={14} /> The Archive
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="mt-8"
        >
          <p className="font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C05C42]">{essay.category}</p>
          <h1 className="mt-5 font-serif text-[clamp(2.2rem,5.5vw,4rem)] font-semibold leading-[1.02] tracking-tight" data-testid="essay-title">
            {essay.title}
          </h1>
          <p className="mt-6 font-body text-[0.72rem] uppercase tracking-[0.2em] text-[#0A0A0A]/50">
            By {essay.author} · {essay.readTime}
          </p>
          <ShareBar title={essay.title} image={essay.image} className="mt-6" />
        </motion.header>

        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease }}
          className="mt-10 aspect-[16/10] overflow-hidden rounded-sm"
        >
          <img src={essay.image} alt={essay.title} className="h-full w-full object-cover" />
        </motion.div>
        {essay.imageCaption && (
          <figcaption className="mt-3 font-body text-[0.7rem] uppercase tracking-[0.16em] text-[#0A0A0A]/45" data-testid="essay-image-caption">
            {essay.imageCaption}
          </figcaption>
        )}

        <div className="mt-12 space-y-7">
          {essay.body.map((p, i) => (
            <p key={i} className="font-body text-[1.05rem] leading-[1.85] text-[#0A0A0A]/80">
              {p}
            </p>
          ))}
        </div>

        {essay.gallery?.length > 0 && (
          <div className="my-12 grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="essay-gallery">
            {essay.gallery.map((g, i) => (
              <figure key={i} className={g.wide ? "sm:col-span-2" : ""}>
                <div className={`overflow-hidden rounded-sm ${g.wide ? "aspect-[16/9]" : "aspect-[4/5]"}`}>
                  <img src={g.src} alt={g.caption || ""} className="h-full w-full object-cover" />
                </div>
                {g.caption && (
                  <figcaption className="mt-3 font-body text-[0.7rem] uppercase tracking-[0.16em] text-[#0A0A0A]/45">
                    {g.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        {essay.secondaryImage && (
          <figure className="my-12">
            <div className="aspect-[16/9] overflow-hidden rounded-sm">
              <img src={essay.secondaryImage} alt="" className="h-full w-full object-cover" />
            </div>
            <figcaption className="mt-3 font-body text-[0.7rem] uppercase tracking-[0.18em] text-[#0A0A0A]/40">
              LALOVAVI — sung in English and Tut, projected in translation.
            </figcaption>
          </figure>
        )}

        {essay.sources?.length > 0 && (
          <div className="mt-14 border-t border-[#0A0A0A]/12 pt-8" data-testid="essay-sources">
            <p className="font-body text-[0.62rem] uppercase tracking-[0.25em] text-[#C05C42]">Further reading</p>
            <ul className="mt-4 space-y-3">
              {essay.sources.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-start gap-2 font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#C05C42]"
                  >
                    <ArrowUpRight size={15} className="mt-0.5 shrink-0 text-[#C05C42]" />
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {essay.downloads?.length > 0 && (
          <div className="mt-8 rounded-xl border border-[#0A0A0A]/12 bg-[#F4EFE6] p-6" data-testid="essay-downloads">
            <p className="font-body text-[0.62rem] uppercase tracking-[0.25em] text-[#C05C42]">Download</p>
            <ul className="mt-4 space-y-3">
              {essay.downloads.map((d) => (
                <li key={d.url}>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-2.5 font-body text-sm font-medium text-[#0A0A0A] transition-colors hover:text-[#C05C42]"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A0A0A] text-[#F4EFE6] transition-colors group-hover:bg-[#C05C42]">
                      <Download size={14} />
                    </span>
                    {d.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-14 flex flex-col items-center gap-6 border-t border-[#0A0A0A]/12 pt-10">
          <ShareBar title={essay.title} image={essay.image} className="justify-center" />
          <div className="w-full rounded-2xl bg-[#0A0A0A] px-7 py-10 text-center">
            <p className="font-serif text-2xl font-light italic text-[#F4EFE6]">
              Culture carried across distance and time.
            </p>
            <a
              href="https://thehorizoncircle.co"
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-block rounded-full bg-[#C05C42] px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="essay-horizon-cta"
            >
              Discover The Horizon Circle
            </a>
          </div>
        </div>
      </article>

      <AlcottFooter onJoin={() => setWaitlist(true)} />
      <HorizonWaitlist open={waitlist} onClose={() => setWaitlist(false)} variant="outer" />
    </div>
  );
};

export default Essay;
